import React, { useMemo, useState } from "react"
import type { DataTableSelection } from "shared/ui/Table/DataTable/DataTable.component"
import { resolveTableStyle } from "shared/ui/Table/style/looks"
import { effectiveTableStyle } from "types/tableStyle.types"
import { TABLE_OPERATION_TYPE } from "types/ReportingDashboard/elements.types"
import type { AxisLeaf, TableModel } from "utils/tableModel"
import {
  DASHBOARD_TABLE_STYLE,
  TableTile as TableTileDoc,
} from "features/Dashboard/types/dashboardDocument"
import {
  useDashboardStore,
  findTile,
} from "features/Dashboard/runtime/dashboardStore"
import { resolveLeafToCondition } from "features/Dashboard/runtime/clickResolver"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import TableFit from "./TableFit.component"
import { useTileTrace } from "./useTileTrace"

type AxisName = "down" | "across"
type Picked = Record<AxisName, Set<string>>

const noPicks = (): Picked => ({ down: new Set(), across: new Set() })

/**
 * Ids of the leaves of `axis` that pass `keep`, each once: a category
 * repeats once per statistic on the statistic axis. Totals and unlabelled
 * leaves never count.
 */
const leafIds = (
  model: TableModel,
  axis: AxisName,
  keep: (leaf: AxisLeaf) => boolean
): string[] => [
  ...new Set(
    model[axis].leaves
      .filter((l) => !l.isTotal && l.id !== "" && keep(l))
      .map((l) => l.id)
  ),
]

/**
 * A table tile. View mode: a header click toggles a cross-filter on its
 * category (when the tile emits them) and the filtered categories stay
 * highlighted. Build mode: header clicks pick rows and columns, and "Remove
 * selected" adds them to the tile's removed lists (XTAB builder tiles only:
 * the rows of a DTAB are numeric variables without categories to drop, so
 * its removed lists change nothing). The grid mounts the tiles
 * afresh on a mode switch, so picks never outlive Build mode. The table fits
 * the tile's width and scrolls when it is taller (see TableFit). In both
 * modes a click on a number opens the trace panel: where it comes from, read
 * off the session's lineage.
 */
const TableTile: React.FC<{ tile: TableTileDoc; model: TableModel }> = ({
  tile,
  model,
}) => {
  const mode = useDashboardStore((s) => s.mode)
  const theme = useDashboardStore((s) => s.document.theme.tableStyle)
  const crossFilter = useDashboardStore((s) => s.crossFilters[tile.id])
  const toggleCrossFilter = useDashboardStore((s) => s.toggleCrossFilter)
  const updateTile = useDashboardStore((s) => s.updateTile)
  const pageId = useDashboardStore(
    (s) => findTile(s, tile.id)?.page.id ?? ""
  )
  const [picked, setPicked] = useState<Picked>(noPicks)
  const { openTrace, panel: tracePanel } = useTileTrace(tile)
  const build = mode === "build"
  const { source } = tile
  const builder = source.kind === "builder"
  const removable =
    source.kind === "builder" &&
    source.operation !== TABLE_OPERATION_TYPE.DTAB
  const transposed = tile.visual.transposed
  // The axis the row variables run along (across under /T).
  const rowsAxis: AxisName = transposed ? "across" : "down"
  const columnsAxis: AxisName = transposed ? "down" : "across"
  const emits = tile.crossFilter.emits && builder
  // Measure rows are not categories: nothing to remove or filter on.
  const measureIds = useMemo(
    () => new Set(tile.measures.map((m) => m.id)),
    [tile.measures]
  )

  const style = useMemo(
    () =>
      resolveTableStyle(
        "report",
        effectiveTableStyle(
          tile.visual.tableStyle,
          theme ?? DASHBOARD_TABLE_STYLE
        )
      ),
    [tile.visual.tableStyle, theme]
  )

  // The categories this tile filters on, as leaf ids on either axis.
  const highlighted = useMemo<Picked>(() => {
    if (!crossFilter || source.kind !== "builder") return noPicks()
    const matches = (axis: AxisName) => (leaf: AxisLeaf) => {
      const cond = resolveLeafToCondition({ leaf, axis, source, transposed })
      return (
        cond !== null &&
        cond.variable === crossFilter.variable &&
        crossFilter.categories.includes(cond.category)
      )
    }
    return {
      down: new Set(leafIds(model, "down", matches("down"))),
      across: new Set(leafIds(model, "across", matches("across"))),
    }
  }, [crossFilter, model, source, transposed])

  // Picks still present in the model shown, by the variables they belong to.
  const pickedRows = leafIds(model, rowsAxis, (l) =>
    picked[rowsAxis].has(l.id)
  )
  const pickedColumns = leafIds(model, columnsAxis, (l) =>
    picked[columnsAxis].has(l.id)
  )

  const onToggle = (axis: AxisName, id: string) => {
    if (axis === "down" && measureIds.has(id)) return
    if (build) {
      setPicked((p) => {
        const next = new Set(p[axis])
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return { ...p, [axis]: next }
      })
      return
    }
    if (!emits || source.kind !== "builder") return
    const leaf = model[axis].leaves.find((l) => l.id === id && !l.isTotal)
    if (!leaf) return
    const cond = resolveLeafToCondition({ leaf, axis, source, transposed })
    if (cond) toggleCrossFilter(tile.id, cond.variable, cond.category)
  }

  const shown = build ? picked : highlighted
  const selection: DataTableSelection = {
    selectedDown: shown.down,
    selectedAcross: shown.across,
    onToggle,
    // Only an XTAB builder tile has removed lists to edit.
    selectable: build ? removable : emits,
  }

  const removeSelected = () => {
    if (pickedRows.length === 0 && pickedColumns.length === 0) return
    updateTile(pageId, tile.id, (t) => {
      if (t.type !== "table" || t.source.kind !== "builder") return t
      const { removed } = t.source
      return {
        ...t,
        source: {
          ...t.source,
          removed: {
            rows: [...new Set([...removed.rows, ...pickedRows])],
            columns: [...new Set([...removed.columns, ...pickedColumns])],
          },
        },
      }
    })
    setPicked(noPicks())
  }

  /** Opens the trace of a cell (see `useTileTrace`). */
  const traceCell = (down: AxisLeaf, across: AxisLeaf, shown: string) =>
    openTrace({ down, across, value: shown })

  // Build mode keeps a bar above a table with removed lists, so the table
  // stays put when the first header is picked.
  const toolbar =
    build && removable ? (
      pickedRows.length + pickedColumns.length > 0 ? (
        <DataQButton type="secondary" size="small" onClick={removeSelected}>
          Remove selected
        </DataQButton>
      ) : (
        <span className="dq-table-fit__hint">
          Click headers to pick rows or columns to remove.
        </span>
      )
    ) : undefined

  return (
    <>
      <TableFit
        model={model}
        style={style}
        fontScale={tile.visual.fontScale}
        selection={selection}
        toolbar={toolbar}
        onCellClick={traceCell}
      />
      {tracePanel}
    </>
  )
}

export default TableTile
